import { z } from "zod";
import { IPQC_REPORT_STAGES, normalizeIpqcReportStage, type IpqcReportStage } from "@apv-erp/status";

// Giai đoạn phiếu IPQC: mã lưu trong DB (mặc định cột là FINAL_BATCH). Mã cũ của form vẫn được đọc.
// Ký mẫu / Cho sản xuất không phải mã giai đoạn riêng (cột sample_approved_at / released_at).
export { IPQC_REPORT_STAGES, type IpqcReportStage };
export const IPQC_REPORT_STAGE_LABEL: Record<IpqcReportStage, string> = {
  SETUP: "Duyệt setup",
  PATROL_IN_PROGRESS: "Kiểm tra lặp lại",
  FINAL_BATCH: "Chốt lô",
};
export function normalizeReportStage(value?: string | null): IpqcReportStage {
  return normalizeIpqcReportStage(value);
}

// Schema for QC Material Inspection (IQC)
export const qcMaterialSchema = z.object({
  id: z.string().optional(),
  materialId: z.string().min(1, "Vui lòng chọn vật tư kiểm tra"),
  vendorId: z.string().optional().nullable(),
  purchaseOrderId: z.string().optional().nullable(),
  receiptDocId: z.string().optional().nullable(),
  templateId: z.string().optional().nullable(),
  inspectorId: z.string().min(1, "Vui lòng chọn người kiểm tra"),
  batchNumber: z.string().min(1, "Vui lòng nhập số lô (Batch/Lot)"),
  invoiceNumber: z.string().optional().nullable(),
  coaNumber: z.string().optional().nullable(),
  materialCategory: z.string().optional().nullable(),
  quantity: z.coerce.number().min(0.01, "Số lượng lô nhập phải > 0"),
  acceptedQty: z.coerce.number().min(0).optional().nullable(),
  rejectedQty: z.coerce.number().min(0).optional().nullable(),
  unit: z.string().min(1, "Vui lòng nhập đơn vị tính").default("Tờ"),
  // AQL ISO 2859-1 & OK/NG Sample Tally
  sampleSize: z.coerce.number().min(0).default(13),
  okSampleQty: z.coerce.number().min(0).optional().nullable(),
  ngSampleQty: z.coerce.number().min(0).optional().nullable(),
  aqlLevel: z.string().default("II"),
  aqlStandard: z.coerce.number().default(1.5),
  acValue: z.coerce.number().min(0).default(0),
  reValue: z.coerce.number().min(1).default(1),
  criticalDefects: z.coerce.number().min(0).default(0),
  majorDefects: z.coerce.number().min(0).default(0),
  minorDefects: z.coerce.number().min(0).default(0),
  // Offset Packaging IQC Quantitative Measurements
  actualGsm: z.coerce.number().optional().nullable(),
  actualCaliper: z.coerce.number().optional().nullable(),
  actualMoisture: z.coerce.number().optional().nullable(),
  actualWidth: z.coerce.number().optional().nullable(),
  actualLength: z.coerce.number().optional().nullable(),
  cobbValue: z.coerce.number().optional().nullable(),
  grainDirection: z.string().optional().nullable(),
  checklist: z.any().optional().nullable(),
  defectDetails: z.any().optional().nullable(),
  technicalParams: z.any().optional().nullable(),
  status: z.enum(["PENDING", "INSPECTING", "PASSED", "WARNING", "FAILED", "REWORK"]).default("PASSED"),
  notes: z.string().optional().nullable(),
  attachments: z.any().optional().nullable(),
});

export type QCMaterialFormValues = z.infer<typeof qcMaterialSchema>;

// Schema for QC Process Inspection (IPQC - 6 Offset Stages + 3-Stage Living Batch Report)
export const qcProcessSchema = z.object({
  id: z.string().optional(),
  productionOrderId: z.string().min(1, "Vui lòng chọn Lệnh Sản Xuất (LSX)"),
  componentId: z.string().optional().nullable(),
  routingStepId: z.string().optional().nullable(),
  stageProcessId: z.string().optional().nullable(),
  processStep: z.string().min(1, "Vui lòng chọn công đoạn"),
  processStage: z
    .enum(["CUTTING", "PRINTING", "LAMINATING", "COATING", "DIE_CUTTING", "GLUING"])
    .default("PRINTING"),
  checkType: z.enum(["FIRST_PIECE", "ROUTINE", "PATROL"]).default("FIRST_PIECE"),
  reportStage: z.preprocess(
    (v) => normalizeReportStage(v as string | null | undefined),
    z.enum(IPQC_REPORT_STAGES),
  ).default("FINAL_BATCH"),
  setupResult: z.enum(["PASSED", "ADJUST_REQUIRED", "FAILED"]).default("PASSED"),
  setupSampleQty: z.coerce.number().min(0).default(5),
  setupDefectQty: z.coerce.number().min(0).default(0),
  setupNotes: z.string().optional().nullable(),
  sampleFrequency: z.string().optional().nullable(),
  patrolLogs: z.any().optional().nullable(),
  machineId: z.string().optional().nullable(),
  operatorName: z.string().optional().nullable(),
  shift: z.string().optional().nullable(),
  productId: z.string().optional().nullable(),
  customerId: z.string().optional().nullable(),
  templateId: z.string().optional().nullable(),
  inspectorId: z.string().min(1, "Vui lòng chọn nhân viên KCS"),
  lotSize: z.coerce.number().optional().nullable(),
  sampleSize: z.coerce.number().min(1, "Số lượng mẫu kiểm tra phải > 0").default(5),
  qtySample: z.coerce.number().min(0).optional().nullable(),
  qtyPassed: z.coerce.number().min(0).optional().nullable(),
  aqlLevel: z.string().default("II"),
  aqlStandard: z.coerce.number().default(1.5),
  acValue: z.coerce.number().default(0),
  reValue: z.coerce.number().default(1),
  defectQty: z.coerce.number().min(0).default(0),
  qtyDefect: z.coerce.number().min(0).optional().nullable(),
  criticalDefects: z.coerce.number().min(0).default(0),
  majorDefects: z.coerce.number().min(0).default(0),
  minorDefects: z.coerce.number().min(0).default(0),
  checklist: z.any().optional().nullable(),
  defectDetails: z.any().optional().nullable(),
  technicalParams: z.any().optional().nullable(),
  status: z.enum(["PENDING", "INSPECTING", "PASSED", "WARNING", "FAILED", "REWORK"]).default("PASSED"),
  notes: z.string().optional().nullable(),
  attachments: z.any().optional().nullable(),
});

export type QCProcessFormValues = z.infer<typeof qcProcessSchema>;

// Schema for QC Final Inspection (Finished Goods - FQC)
export const qcFinalSchema = z
  .object({
    id: z.string().optional(),
    productionOrderId: z.string().min(1, "Vui lòng chọn Lệnh Sản Xuất"),
    orderId: z.string().optional().nullable(),
    productId: z.string().optional().nullable(),
    productTypeId: z.string().optional().nullable(),
    customerId: z.string().optional().nullable(),
    templateId: z.string().optional().nullable(),
    inspectorId: z.string().min(1, "Vui lòng chọn người kiểm tra"),
    qtyChecked: z.coerce.number().min(1, "Số lượng kiểm tra phải lớn hơn 0"),
    qtyPassed: z.coerce.number().min(0, "Số lượng đạt không hợp lệ"),
    qtyFailed: z.coerce.number().min(0, "Số lượng lỗi không hợp lệ"),
    // AQL Parameters, Sample OK/NG & Packaging
    aqlLevel: z.string().optional().default("II"),
    aqlStandard: z
      .preprocess((val) => {
        if (val === undefined || val === null || val === "") return 1.5;
        if (typeof val === "number") return isNaN(val) ? 1.5 : val;
        if (typeof val === "string") {
          const parsed = parseFloat(val);
          return isNaN(parsed) ? 1.5 : parsed;
        }
        return 1.5;
      }, z.number().default(1.5))
      .optional(),
    lotSize: z.coerce.number().optional(),
    sampleSize: z.coerce.number().optional(),
    okSampleQty: z.coerce.number().min(0).optional().nullable(),
    ngSampleQty: z.coerce.number().min(0).optional().nullable(),
    acValue: z.coerce.number().optional(),
    reValue: z.coerce.number().optional(),
    packagingCheck: z.boolean().optional(),
    // Số CoA (chất lượng). Số lô, quy cách thùng, nhập kho thuộc Phiếu đóng gói (Sản xuất)
    coaNo: z.string().optional().nullable(),
    criticalDefects: z.coerce.number().optional().default(0),
    majorDefects: z.coerce.number().optional().default(0),
    minorDefects: z.coerce.number().optional().default(0),
    // FQC Quantitative Dimensions & Tests
    actualLength: z.coerce.number().optional().nullable(),
    actualWidth: z.coerce.number().optional().nullable(),
    actualHeight: z.coerce.number().optional().nullable(),
    moisture: z.coerce.number().optional().nullable(),
    barcodeGrade: z.string().optional().nullable(),
    dropTestResult: z.string().optional().nullable(),
    fittingTestResult: z.string().optional().nullable(),
    technicalParams: z.any().optional().nullable(),
    attachments: z.any().optional().nullable(),
    checklist: z.any().optional().nullable(),
    defectDetails: z.any().optional().nullable(),
    status: z.enum(["PENDING", "INSPECTING", "PASSED", "WARNING", "FAILED"]),
    notes: z.string().optional(),
  })
  .refine((data) => data.qtyChecked === data.qtyPassed + data.qtyFailed, {
    message: "Tổng số lượng đạt và lỗi phải bằng số lượng kiểm tra",
    path: ["qtyChecked"],
  });

export type QCFinalFormValues = z.infer<typeof qcFinalSchema>;

// Schema for NCR (Non-Conformance Report - ISO 9001 §8.7 & CAPA 8D)
export const ncrSchema = z.object({
  id: z.string().optional(),
  source: z.enum(["INTERNAL", "CUSTOMER", "SUPPLIER"]).default("INTERNAL"),
  sourceType: z.string().optional().nullable(), // IQC | IPQC | FQC | SHOPFLOOR | CUSTOMER_CLAIM
  relatedType: z.string().optional().nullable(),
  relatedId: z.string().optional().nullable(),
  qcMaterialId: z.string().optional().nullable(),
  qcProcessId: z.string().optional().nullable(),
  qcFinalId: z.string().optional().nullable(),
  productionOrderId: z.string().optional().nullable(),
  routingStepId: z.string().optional().nullable(),
  processStage: z.string().optional().nullable(),
  machineId: z.string().optional().nullable(),
  materialId: z.string().optional().nullable(),
  vendorId: z.string().optional().nullable(),
  productId: z.string().optional().nullable(),
  customerId: z.string().optional().nullable(),
  batchNumber: z.string().optional().nullable(),
  lotSize: z.coerce.number().min(0).optional().nullable(),
  checkedQty: z.coerce.number().min(0).optional().nullable(),
  sampleSize: z.coerce.number().optional().nullable(),
  okQty: z.coerce.number().min(0).optional().nullable(),
  defectQty: z.coerce.number().min(0).optional().nullable(),
  unit: z.string().optional().nullable(),
  issueSummary: z.string().min(3, "Mô tả sự cố không phù hợp phải có ít nhất 3 ký tự"),
  failedCriteria: z.any().optional().nullable(),
  defectDetails: z.any().optional().nullable(),
  defectCategory6M: z.string().optional().nullable(),
  rootCause: z.string().optional().nullable(),
  whyAnalysis: z.any().optional().nullable(),
  disposition: z.string().optional().nullable(),
  containmentAction: z.string().optional().nullable(),
  correctiveAction: z.string().optional().nullable(),
  preventiveAction: z.string().optional().nullable(),
  resolutionResult: z.string().optional().nullable(),
  responsibleDept: z.string().optional().nullable(),
  severity: z.enum(["LOW", "MEDIUM", "HIGH", "CRITICAL"]).default("HIGH"),
  status: z.enum(["OPEN", "INVESTIGATING", "CORRECTIVE_ACTION", "VERIFICATION", "CLOSED"]).default("OPEN"),
  costImpact: z.coerce.number().min(0).optional().nullable(),
  costBreakdown: z.any().optional().nullable(),
  ownerId: z.string().optional().nullable(),
  attachments: z.any().optional().nullable(),
});

export type NCRFormValues = z.infer<typeof ncrSchema>;
